/**
 * Small top-right pill — "Esc 跳过 · Tab 菜单" — shown for a few seconds whenever a cinematic
 * registers ctx.skipHandler (Esc is the skip channel then; Tab / M open the pause menu). Hidden on
 * touch layouts (the 菜单 pill is already on screen) and while any modal is open.
 */
import Phaser from 'phaser';
import { GAME_W } from '@/core/config';
import type { UiRoot } from './UiRoot';
import { uiText } from './text';
import { Css, Slices, UiDepth } from './theme';

export const SKIP_HINT_MS = 4000;
/** Minimum gap before the pill is shown again for a new cinematic (memory stages chain quickly). */
export const SKIP_HINT_RESHOW_MS = 8000;
export const SKIP_HINT_TEXT = 'Esc 跳过 · Tab 菜单';

export class SkipHint {
  readonly container: Phaser.GameObjects.Container;
  private readonly label: Phaser.GameObjects.Text;
  private lastHandler: unknown = null;
  private shownAt = -Infinity;
  private hideAt = 0;
  private shown = false;

  constructor(private readonly root: UiRoot, private readonly touch: () => boolean) {
    const s = root.scene;
    this.container = s.add.container(GAME_W - 8, 8).setDepth(UiDepth.PROMPT).setVisible(false).setAlpha(0);
    this.label = uiText(s, -8, 11, SKIP_HINT_TEXT, { fontSize: '10px', color: Css.parchment }).setOrigin(1, 0.5);
    const bar = s.add.nineslice(0, 0, Slices.barDim.key, undefined, Math.round(this.label.width) + 16, 22, Slices.barDim.l, Slices.barDim.r, Slices.barDim.t, Slices.barDim.b).setOrigin(1, 0);
    this.container.add([bar, this.label]);
    root.onUpdate((t) => this.refresh(t));
    root.track(() => { s.tweens.killTweensOf(this.container); this.container.destroy(true); });
  }

  get isShown(): boolean { return this.shown; }
  get text(): string { return this.label.text; }

  private refresh(now: number): void {
    const handler = this.root.ctx?.skipHandler ?? null;
    if (handler !== this.lastHandler) {
      this.lastHandler = handler;
      if (handler && now - this.shownAt > SKIP_HINT_RESHOW_MS) { this.shownAt = now; this.hideAt = now + SKIP_HINT_MS; }
      if (!handler) this.hideAt = 0;
    }
    const want = Boolean(handler) && now < this.hideAt && !this.root.hasModal() && !this.touch();
    if (want === this.shown) return;
    this.shown = want;
    const s = this.root.scene;
    const ms = this.root.reduceMotion() ? 0 : want ? 150 : 250;
    s.tweens.killTweensOf(this.container);
    if (want) {
      this.container.setVisible(true);
      s.tweens.add({ targets: this.container, alpha: 1, duration: ms });
    } else {
      s.tweens.add({ targets: this.container, alpha: 0, duration: ms, onComplete: () => { if (!this.shown) this.container.setVisible(false); } });
    }
  }
}
